import { useCallback, useEffect, useRef, useState } from "react";
import { ForceGraph2D } from "react-force-graph";
import { GraphData } from "../types/GraphData";
import { BLUE, GREEN, GREY, NAVY, ORANGE, RED, WHITE } from "../utils/Colors";

type SelectedNode = {
  node: string;
  ancestors: Set<string>;
  descendants: Set<string>;
};

type SelectedCycle = {
  nodes: Set<string>;
  links: Map<string, number>; // Source + Target => How many particles emitted
};

type Props = {
  graphData: GraphData;
  onNodeClick: (id: string) => void;
  clearClick: () => void;
  selectedNode: SelectedNode | null;
  selectedCycle: SelectedCycle | null;
};

function GraphVisual({
  graphData,
  onNodeClick,
  clearClick,
  selectedNode,
  selectedCycle
}: Props) {
  const paint = useCallback(
    (node: any, ctx: any) => {
      const isSelectedNode = node.id === selectedNode?.node;
      const fontSize = isSelectedNode ? 4 : 3;
      ctx.font = `${fontSize}px Sans-Serif`;
      const textWidth = ctx.measureText(node.id).idth;
      const bckgDimensions = [textWidth, fontSize].map(n => n + fontSize * 0.2); // some padding

      if (isSelectedNode) {
        ctx.fillStyle = "rgba(200, 200, 200, 0.8)";
        ctx.fillRect(
          node.x - bckgDimensions[0] / 2,
          node.y - bckgDimensions[1] / 2 - fontSize * 1.5,
          ...bckgDimensions
        );
      }
      ctx.fillStyle = isSelectedNode ? RED : NAVY;
      ctx.fillStyle = "rgba(0, 0, 0, 1)";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(node.id, node.x, node.y - fontSize * 1.5);

      node.__bckgDimensions = bckgDimensions;
    },
    [selectedNode]
  );

  function linkInCycle(link: any) {
    const key = link?.source?.id + link?.target?.id;
    return selectedCycle?.links.get(key);
  }

  function linkColor(link: any) {
    if (link?.source?.id === selectedNode?.node) {
      return GREEN;
    } else if (link?.target?.id === selectedNode?.node) {
      return ORANGE;
    } else if (linkInCycle(link)) {
      return BLUE;
    }
    return GREY;
  }

  function nodeColor(node: any) {
    if (node.id === selectedNode?.node) {
      return RED;
    } else if (selectedNode?.descendants.has(node.id)) {
      return GREEN;
    } else if (selectedNode?.ancestors.has(node.id)) {
      return ORANGE;
    } else if (selectedCycle?.nodes.has(node.id)) {
      return BLUE;
    }
    return NAVY;
  }

  const graph = useRef<any>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Keep the canvas sized to its container (works for both the desktop
  // side-by-side layout and the stacked mobile layout).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const update = () =>
      setSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const selectionKey = selectedNode?.node ?? Array.from(selectedCycle?.nodes ?? []).join("|");

  useEffect(() => {
    if (!graph.current) {
      return;
    }

    if (!selectionKey) {
      // Keep the unselected view simple and stable. The force layout is
      // centered around (0, 0), so Back just returns the camera there at a
      // fixed zoom. No zoomToFit, no delayed follow-up animation.
      graph.current.centerAt(0, 0, 350);
      graph.current.zoom(1.1, 350);
      return;
    }

    graph.current.zoomToFit(
      350,
      120,
      (n: any) =>
        selectedNode?.node === n.id ||
        selectedNode?.ancestors.has(n.id) ||
        selectedNode?.descendants.has(n.id) ||
        selectedCycle?.nodes.has(n.id)
    );
  // Only rerun when the logical selection changes. Including the Set objects
  // themselves makes the camera effect fire on ordinary renders, which feels
  // jumpy on mobile.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", overflow: "hidden" }}
    >
    <ForceGraph2D
      ref={graph}
      width={size.width}
      height={size.height}
      graphData={graphData}
      linkDirectionalArrowLength={2}
      linkDirectionalArrowRelPos={1}
      onNodeClick={(node, _) => {
        onNodeClick(node.id!.toString());
      }}
      onBackgroundClick={_ => clearClick()}
      linkColor={linkColor}
      linkWidth={link => (linkInCycle(link) ? 3 : 1)}
      linkDirectionalParticleWidth={link => (linkInCycle(link) ? 4 : 0)}
      linkDirectionalParticles={link => linkInCycle(link) ?? 0}
      nodeVal={node => (node.id === selectedNode?.node ? 0.6 : 0.3)}
      nodeColor={nodeColor}
      nodeCanvasObjectMode={_ => "after"}
      nodeCanvasObject={paint}
      backgroundColor={WHITE}
      minZoom={1}
      maxZoom={10}
    ></ForceGraph2D>
    </div>
  );
}

export default GraphVisual;
